import { useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { Form } from "@/react/components/shared/uikit/Form";
import { buildTransformInputValueToCurrencyAmountStrFunc } from "@circle-react/components/Paywalls/Admin/utils";
import type { ReducedPrice } from "@circle-react/types/PaywallCustomization";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { PricingTabPriceListCellPricing } from "../../../PricingTab/StripePricingTab/PriceList/CellPricing";
import { usePricingTabContext } from "../../../PricingTab/StripePricingTab/hooks/usePricingTabContext";
import { localeRoot } from "../../constants";
import { PriceField } from "./Fields/PriceField";

interface PriceFormProps {
  index: number;
  reducedPrice: ReducedPrice;
}

const formRootName =
  "paywall_customization_attributes.reduced_price_properties.prices";

export const PriceForm = ({ index, reducedPrice }: PriceFormProps) => {
  const { watch } = useFormContext();
  const stripePrices = watch("stripe_prices_attributes");
  const isPriceEnabled = watch([formRootName, index, "enabled"].join("."));

  const findPaywallPrice: any = useCallback(
    () =>
      stripePrices.find((price: any) =>
        [price.id, price.tmp_id].includes(reducedPrice.price_id),
      ),
    [stripePrices, reducedPrice?.price_id],
  );
  const { currency } = usePricingTabContext();

  const paywallPrice = findPaywallPrice();
  const transform = buildTransformInputValueToCurrencyAmountStrFunc(currency);

  if (!paywallPrice) {
    return null;
  }

  return (
    <div
      className="border-primary flex flex-col gap-y-3 border-b px-6 py-5"
      data-testid="reduced-price-form-group"
    >
      <div className="flex flex-row justify-between">
        <div className="flex flex-col gap-y-1">
          <Typography.LabelSm weight="medium">
            {t([localeRoot, "builder.reduced_prices.fields.price_index"], {
              row_number: index + 1,
            })}
          </Typography.LabelSm>
          <Typography.BodySm color="text-secondary">
            <PricingTabPriceListCellPricing
              row={{ original: paywallPrice }}
              options={{
                shouldShowCurrency: false,
              }}
            />
          </Typography.BodySm>
        </div>
        <Form.ToggleSwitch
          name={[formRootName, index, "enabled"].join(".")}
          variant="small"
          shouldUseCheckmark={false}
          dataTestId="reduced-price-toggle"
        />
      </div>
      {isPriceEnabled && (
        <div className="grid grid-cols-9 gap-x-3 gap-y-2">
          <span className="col-span-4">
            <Typography.LabelXs
              textTransform="uppercase"
              color="text-tertiary"
              weight="medium"
            >
              {t([localeRoot, "builder.reduced_prices.fields.before_price"])}
            </Typography.LabelXs>
          </span>
          <span />
          <span className="col-span-4">
            <Typography.LabelXs
              textTransform="uppercase"
              color="text-tertiary"
              weight="medium"
            >
              {t([localeRoot, "builder.reduced_prices.fields.your_price"])}
            </Typography.LabelXs>
          </span>
          {(paywallPrice.upfront_payment_amount || 0) > 0 && (
            <PriceField
              name="upfront_amount"
              index={index}
              transformValue={transform}
            />
          )}
          <PriceField name="amount" index={index} transformValue={transform} />
        </div>
      )}
    </div>
  );
};
